/**
 * Presale API Client — Talks to Forge service for presale operations.
 * Separate from the main api-client.ts because Forge runs on a different URL.
 */

const FORGE_API_URL = import.meta.env.VITE_API_URL || '';
const FORGE_API_TOKEN = import.meta.env.VITE_FORGE_API_TOKEN || '';
const MOCK_MODE = import.meta.env.VITE_MOCK_API === 'true';

interface ForgeResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

function getUserId(): string {
  // Extract wallet address from Privy localStorage
  try {
    const raw = localStorage.getItem('privy:connections');
    if (raw) {
      const connections = JSON.parse(raw);
      const wallet = (connections as Array<{ type: string; address?: string }>)?.find(
        (c) => c.type === 'wallet'
      );
      if (wallet?.address) return wallet.address;
    }
  } catch {
    /* fallback */
  }
  return 'anonymous';
}

const MOCK_PRESALES: Presale[] = [
  {
    id: 'mock-1',
    tokenName: 'MOON PEPE',
    tokenSymbol: 'MPEPE',
    description: 'The most based frog on Solana. Block-0 launch, holders from genesis.',
    imageUrl: 'https://placehold.co/400x400/1a1a1a/c4f70e?text=MPEPE',
    targetSol: 3,
    minSol: 1,
    maxBuyers: 3,
    minContribution: 0.1,
    maxContribution: 5,
    totalRaised: 1.5,
    buyerCount: 1,
    status: 'OPEN',
    epochCadence: 'DAILY',
    startsAt: new Date().toISOString(),
    endsAt: new Date(Date.now() + 3600000 * 4).toISOString(),
    createdAt: new Date().toISOString(),
  },
  {
    id: 'mock-2',
    tokenName: 'DEGEN CAT',
    tokenSymbol: 'DGCAT',
    description: 'Community-owned cat token. AI-driven post-launch automation.',
    imageUrl: 'https://placehold.co/400x400/1a1a1a/c4f70e?text=DGCAT',
    targetSol: 3,
    minSol: 1,
    maxBuyers: 3,
    minContribution: 0.1,
    maxContribution: 5,
    totalRaised: 3,
    buyerCount: 3,
    status: 'LIVE',
    epochCadence: 'DAILY',
    startsAt: new Date(Date.now() - 86400000).toISOString(),
    endsAt: new Date(Date.now() - 3600000).toISOString(),
    createdAt: new Date(Date.now() - 86400000).toISOString(),
  },
];

async function forgeRequest<T>(
  path: string,
  options: { method?: string; body?: unknown } = {}
): Promise<T> {
  if (MOCK_MODE && (!options.method || options.method === 'GET')) {
    await new Promise((r) => setTimeout(r, 300));
    if (path === '/discover') return MOCK_PRESALES as T;
    if (path.startsWith('/mock-')) return MOCK_PRESALES.find((p) => p.id === path.slice(1)) as T;
    return MOCK_PRESALES as T;
  }
  const url = `${FORGE_API_URL}/api/forge/presale${path}`;
  const init: RequestInit = {
    method: options.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': FORGE_API_TOKEN,
      'x-user-id': getUserId(),
    },
  };

  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
  }

  const response = await fetch(url, init);

  const data = (await response.json()) as ForgeResponse<T>;

  if (!response.ok || !data.success) {
    throw new Error(data.error || `Request failed: ${response.status}`);
  }

  return data.data;
}

// Also expose forge-level endpoints (wallets, launches, bots)
async function forgeBaseRequest<T>(
  path: string,
  options: { method?: string; body?: unknown } = {}
): Promise<T> {
  const url = `${FORGE_API_URL}/api/forge${path}`;
  const init: RequestInit = {
    method: options.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': FORGE_API_TOKEN,
      'x-user-id': getUserId(),
    },
  };

  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
  }

  const response = await fetch(url, init);

  const data = (await response.json()) as ForgeResponse<T>;

  if (!response.ok || !data.success) {
    throw new Error(data.error || `Request failed: ${response.status}`);
  }

  return data.data;
}

// ── Whitelist ──────────────────────────────────────────

export interface LauncherInfo {
  id: string;
  userId: string;
  displayName?: string;
  presaleCount: number;
}

export interface InviteCode {
  id: string;
  code: string;
  status: string;
  usedById?: string;
  usedAt?: string;
  expiresAt?: string;
  createdAt: string;
}

export const whitelistApi = {
  check: () =>
    forgeRequest<{ whitelisted: boolean; launcher: LauncherInfo | null }>('/whitelist/check'),
  generateCodes: (count = 3) =>
    forgeRequest<InviteCode[]>('/invite/generate', { method: 'POST', body: { count } }),
  redeemCode: (code: string, displayName?: string) =>
    forgeRequest<LauncherInfo>('/invite/redeem', { method: 'POST', body: { code, displayName } }),
  listCodes: () => forgeRequest<InviteCode[]>('/invite/list'),
};

// ── Presale ────────────────────────────────────────────

export interface PresaleCreateParams {
  tokenName: string;
  tokenSymbol: string;
  description?: string;
  imageUrl?: string;
  twitter?: string;
  website?: string;
  targetSol: number;
  minSol: number;
  maxBuyers?: number;
  minContribution?: number;
  maxContribution?: number;
  durationMinutes: number;
  epochCadence?: 'DAILY' | 'WEEKLY';
  pitch?: string;
  useOfFunds?: Array<{ label: string; percentage: number }>;
}

export interface Presale {
  id: string;
  tokenName: string;
  tokenSymbol: string;
  description?: string;
  imageUrl?: string;
  twitter?: string;
  website?: string;
  pitch?: string;
  useOfFunds?: Array<{ label: string; percentage: number }>;
  targetSol: number;
  minSol: number;
  maxBuyers: number;
  minContribution: number;
  maxContribution: number;
  status: string;
  totalRaised: number;
  buyerCount: number;
  startsAt: string;
  endsAt: string;
  tokenMint?: string;
  forgeLaunchId?: string;
  epochCadence: string;
  launchedAt?: string;
  createdAt: string;
  launcher?: {
    displayName?: string;
    presaleCount?: number;
    avatarUrl?: string;
    twitterHandle?: string;
  };
  contributions?: PresaleContribution[];
  rewardVault?: { currentBalanceSol: number; totalCollected: number; totalDistributed: number };
  _count?: { contributions: number };
}

export interface PresaleContribution {
  id: string;
  buyerUserId: string;
  amountSol: number;
  percentage: number;
  buyerPublicKey: string;
  confirmed: boolean;
  tokensReceived?: number;
  totalRewardsSol: number;
}

export const presaleApi = {
  create: (params: PresaleCreateParams) =>
    forgeRequest<Presale>('/create', { method: 'POST', body: params }),
  list: (filters?: { status?: string; launcherId?: string }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.launcherId) params.set('launcherId', filters.launcherId);
    const qs = params.toString();
    return forgeRequest<Presale[]>(`/list${qs ? `?${qs}` : ''}`);
  },
  discover: () => forgeRequest<Presale[]>('/discover'),
  get: (id: string) => forgeRequest<Presale>(`/${id}`),
  publish: (id: string) => forgeRequest<Presale>(`/${id}/publish`, { method: 'POST' }),
  cancel: (id: string) => forgeRequest<void>(`/${id}/cancel`, { method: 'POST' }),
  contribute: (id: string, amountSol: number, buyerPublicKey: string) =>
    forgeRequest<PresaleContribution>(`/${id}/contribute`, {
      method: 'POST',
      body: { amountSol, buyerPublicKey },
    }),
  getContributions: (id: string) => forgeRequest<PresaleContribution[]>(`/${id}/contributions`),
  getMyContribution: (id: string) =>
    forgeRequest<PresaleContribution | null>(`/${id}/my-contribution`),
  launch: (id: string) => forgeRequest<{ tokenMint: string }>(`/${id}/launch`, { method: 'POST' }),
};

// ── Reward Vault ───────────────────────────────────────

export interface VaultStatus {
  currentBalanceSol: number;
  totalCollected: number;
  totalDistributed: number;
  epochCadence: string;
  currentEpoch: RewardEpoch | null;
  nextDistributionAt: string | null;
}

export interface RewardEpoch {
  id: string;
  epochNumber: number;
  status: string;
  amountToDistribute: number;
  amountDistributed: number;
  platformFeeSol: number;
  periodStart: string;
  periodEnd: string;
  distributedAt?: string;
  payouts?: RewardPayout[];
}

export interface RewardPayout {
  buyerUserId: string;
  amountSol: number;
  paid: boolean;
  paidAt?: string;
}

export interface BotStatus {
  autoSell: { active: boolean };
  volumeBot: { active: boolean };
  sniperGuard: { active: boolean };
}

export interface BotConfig {
  mode?: string;
  [key: string]: unknown;
}

export const vaultApi = {
  getStatus: (presaleId: string) => forgeRequest<VaultStatus>(`/${presaleId}/vault`),
  claimFees: (presaleId: string) =>
    forgeRequest<{ claimedSol: number }>(`/${presaleId}/vault/claim-fees`, { method: 'POST' }),
  distribute: (presaleId: string, epochId: string) =>
    forgeRequest<{ distributed: number; payouts: number }>(`/${presaleId}/vault/distribute`, {
      method: 'POST',
      body: { epochId },
    }),
  getEpochs: (presaleId: string) => forgeRequest<RewardEpoch[]>(`/${presaleId}/vault/epochs`),
  getMyRewards: (presaleId: string) =>
    forgeRequest<{ totalRewardsSol: number; payouts: RewardPayout[] }>(
      `/${presaleId}/vault/my-rewards`
    ),
};

// ── Bot Controls (delegates to forge) ──────────────────

export const botApi = {
  configureAutoSell: (launchId: string, config: BotConfig) =>
    forgeBaseRequest<void>('/bots/auto-sell', { method: 'POST', body: { launchId, config } }),
  stopAutoSell: (launchId: string) =>
    forgeBaseRequest<void>('/bots/auto-sell', {
      method: 'POST',
      body: { launchId, action: 'stop' },
    }),
  configureVolume: (launchId: string, config: BotConfig) =>
    forgeBaseRequest<void>('/bots/volume', { method: 'POST', body: { launchId, config } }),
  stopVolume: (launchId: string) =>
    forgeBaseRequest<void>('/bots/volume', { method: 'POST', body: { launchId, action: 'stop' } }),
  getStatus: (launchId: string) => forgeBaseRequest<BotStatus>(`/bots/status/${launchId}`),
};
